import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";

const CartContext = createContext(null);
const STORAGE_KEY = "sd_cart_v1";

const readStorage = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch { return []; }
};

export function CartProvider({ children }) {
  const [items, setItems] = useState(readStorage);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(items)); } catch { /* ignore quota */ }
  }, [items]);

  const addItem = useCallback((item) => {
    setItems((prev) => {
      const key = `${item.slug}::${item.size || ""}`;
      const idx = prev.findIndex((x) => `${x.slug}::${x.size || ""}` === key);
      if (idx >= 0) {
        const next = [...prev];
        next[idx] = { ...next[idx], qty: next[idx].qty + (item.qty || 1) };
        return next;
      }
      return [...prev, { ...item, qty: item.qty || 1 }];
    });
    setOpen(true);
  }, []);

  const updateQty = useCallback((slug, size, qty) => {
    setItems((prev) => prev
      .map((x) => (x.slug === slug && (x.size || "") === (size || "") ? { ...x, qty: Math.max(1, qty) } : x))
    );
  }, []);

  const removeItem = useCallback((slug, size) => {
    setItems((prev) => prev.filter((x) => !(x.slug === slug && (x.size || "") === (size || ""))));
  }, []);

  const clear = useCallback(() => setItems([]), []);

  const { count, subtotal } = useMemo(() => {
    let c = 0, s = 0;
    items.forEach((it) => {
      c += it.qty;
      const unit = parseFloat(String(it.price || "").replace(/[^\d.]/g, "")) || 0;
      s += unit * it.qty;
    });
    return { count: c, subtotal: s };
  }, [items]);

  const value = useMemo(() => ({
    items, count, subtotal, open, setOpen,
    addItem, updateQty, removeItem, clear,
  }), [items, count, subtotal, open, addItem, updateQty, removeItem, clear]);

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart() {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used inside CartProvider");
  return ctx;
}
